import LoadingSpinner from "@/components/Common/Spinner";
import { Breadcrumb } from "@/components/Common/Breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useGetEnvs } from "@/hooks/useEnvs";
import {
  useGetGitHubBranches,
  useGetGitHubInstallations,
} from "@/hooks/useGitHubApp";
import { useGetRegions } from "@/hooks/useRegions";
import { FALLBACK_ERROR_MESSAGE } from "@/lib/constants";
import { createDeploy } from "@/lib/deploy/api";
import { PATH, APPLICATION_CATEGORY } from "@/lib/enums";
import { humanizeString } from "@/lib/utils";
import {
  createDeploySchema,
  type CreateDeployFormData,
} from "@/lib/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Github,
  Globe,
  Loader2,
  Plus,
  Server,
  Trash2,
  Upload,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Utility to parse .env-style content into [{ key, value }]
 */
function parseEnvContent(
  content: string,
): Array<{ key: string; value: string }> {
  if (!content) return [];
  const lines = content.split(/\r?\n/);
  const envVars: Array<{ key: string; value: string }> = [];

  for (const raw of lines) {
    const line = raw.trim();

    // skip comments / empty
    if (!line || line.startsWith("#")) continue;

    // split on first '='
    const idx = line.indexOf("=");
    if (idx === -1) continue;

    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();

    // remove surrounding quotes
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }

    if (key) {
      envVars.push({ key, value });
    }
  }

  return envVars;
}

/**
 * Paste/Drop area for .env content
 */
function EnvPasteArea({
  onPasteVars,
  disabled,
}: {
  onPasteVars: (vars: Array<{ key: string; value: string }>) => void;
  disabled?: boolean;
}) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handlePaste: React.ClipboardEventHandler<HTMLDivElement> = (e) => {
    if (disabled) return;
    const pastedText = e.clipboardData?.getData("text") ?? "";
    if (pastedText.trim()) {
      const parsedVars = parseEnvContent(pastedText);
      if (parsedVars.length) onPasteVars(parsedVars);
    }
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);

    // 1) if user drops a file, read it
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length) {
      const text = await files[0].text();
      const parsedVars = parseEnvContent(text);
      if (parsedVars.length) onPasteVars(parsedVars);
      return;
    }

    // 2) else try text drop
    const textData = e.dataTransfer?.getData("text") ?? "";
    if (textData.trim()) {
      const parsedVars = parseEnvContent(textData);
      if (parsedVars.length) onPasteVars(parsedVars);
    }
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors outline-none ${
        isDragOver
          ? "border-cyan-500 bg-cyan-500/10"
          : "border-slate-600 hover:border-cyan-500/50"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-text"}`}
      onPaste={handlePaste}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      tabIndex={0}
    >
      <div className="text-slate-400 text-sm">
        <div className="font-medium mb-1">
          Paste or drop your <code>.env</code> here
        </div>
        <div>Press Ctrl/Cmd+V to paste, or drop a .env file</div>
      </div>
    </div>
  );
}

export function CreateDeployPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [selectedRepo, setSelectedRepo] = useState<{
    owner: string;
    repoName: string;
    name: string;
    installationId: number;
  } | null>(null);

  // Determine breadcrumb and sidebar selection based on referrer
  const referrerInfo = useMemo(() => {
    const state = location.state as any;
    const referrerPath = state?.from || PATH.HOME;

    // Check if coming from Applications page with category filter
    if (referrerPath === PATH.APPLICATIONS) {
      const search = state?.search || "";
      const params = new URLSearchParams(search);
      const category = params.get("category");

      if (category === APPLICATION_CATEGORY.STATIC_SITES) {
        return {
          label: "Static sites",
          href: `${PATH.APPLICATIONS}?category=${APPLICATION_CATEGORY.STATIC_SITES}`,
          search,
          category: APPLICATION_CATEGORY.STATIC_SITES,
        };
      } else if (category === APPLICATION_CATEGORY.DATABASES) {
        return {
          label: "Databases",
          href: `${PATH.APPLICATIONS}?category=${APPLICATION_CATEGORY.DATABASES}`,
          search,
          category: APPLICATION_CATEGORY.DATABASES,
        };
      }

      return {
        label: "Applications",
        href: PATH.APPLICATIONS,
        search,
        category: APPLICATION_CATEGORY.APPLICATIONS,
      };
    }

    // Default to Dashboard
    return {
      label: "Dashboard",
      href: PATH.HOME,
      search: "",
      category: null,
    };
  }, [location.state, location.pathname]);

  const repoQuery = useGetGitHubInstallations(true);
  const regionQuery = useGetRegions();
  const envQuery = useGetEnvs();

  const branchQuery = useGetGitHubBranches(
    selectedRepo?.repoName ?? "",
    selectedRepo?.installationId ?? null,
    !!selectedRepo,
  );

  const createDeployMutation = useMutation({
    mutationFn: createDeploy,
    onSuccess: (data) => {
      queryClient.setQueryData(["deploys"], (old: any) => [
        data,
        ...(old || []),
      ]);
      queryClient.invalidateQueries({ queryKey: ["deploys"] });
    },
    onError: (error: any) => {
      console.error("Deploy operation failed.");
      toast({
        title: "Error",
        description: error?.response?.data?.message ?? FALLBACK_ERROR_MESSAGE,
        variant: "destructive",
      });
    },
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<CreateDeployFormData>({
    resolver: zodResolver(createDeploySchema),
    defaultValues: {
      deployType: "SHARED_COMPUTE_INSTANCE",
      sourceType: "github",
      environmentVariables: [],
      region: "",
      env: "",
      repoName: "",
      repoUrl: undefined,
      branch: "",
      installationId: undefined,
      outputDirectory: "",
      port: undefined,
      rootDirectory: "",
      autoDeployEnabled: false,
    },
  });

  useEffect(() => {
    const firstRegion = regionQuery.data?.[0] ?? "";
    const firstEnv = envQuery.data?.[0] ?? "";
    reset((previous) => ({
      ...previous,
      region: previous.region || firstRegion,
      env: previous.env || firstEnv,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionQuery.data, envQuery.data, reset]);

  useEffect(() => {
    if (referrerInfo.category === APPLICATION_CATEGORY.STATIC_SITES) {
      setValue("deployType", "CDN_SITE", { shouldValidate: false });
    }
  }, [referrerInfo.category, setValue]);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "environmentVariables",
  });

  const deployType = watch("deployType");
  const sourceType = watch("sourceType");
  const branchValue = watch("branch");
  const regionValue = watch("region");
  const envValue = watch("env");
  const outputDirValue = watch("outputDirectory");
  const portValue = watch("port");
  const repoNameValue = watch("repoName");
  const rootDirectoryValue = watch("rootDirectory");

  const addEnvVar = () => append({ key: "", value: "" });

  const onSubmit = async (data: CreateDeployFormData) => {
    try {
      const payload: CreateDeployFormData = {
        ...data,
        outputDirectory:
          data.deployType === "CDN_SITE" ? data.outputDirectory || "dist" : "",
      };
      const res = await createDeployMutation.mutateAsync(payload);
      toast({
        title: "Deploy created",
        description: "Your deploy has been created successfully.",
      });
      navigate(`${PATH.APPLICATIONS}/${res.data.id}`);
    } catch (error: any) {
      console.error("Deploy creation failed.");
      toast({
        title: error?.response?.data?.message ?? FALLBACK_ERROR_MESSAGE,
        variant: "destructive",
      });
    }
  };

  const handleDeployTypeChange = (
    type: "CDN_SITE" | "SHARED_COMPUTE_INSTANCE",
  ) => {
    setValue("deployType", type, { shouldValidate: true });

    if (type === "CDN_SITE") {
      setValue("port", undefined, { shouldValidate: true });
    }
  };

  const isCreating = createDeployMutation.isPending || isSubmitting;

  const regions = regionQuery.data ?? [];
  const envs = envQuery.data ?? [];

  const branchDisabled = !selectedRepo || branchQuery.isLoading;

  const repoOptions = useMemo(() => {
    const items = repoQuery.data?.items ?? [];
    return items.flatMap((item: any) =>
      (item.repositories ?? []).map((repo: any) => ({
        ...repo,
        installationId: item.installation_id,
      })),
    );
  }, [repoQuery.data]);

  // Find the selected repo ID from the form value
  const selectedRepoId = useMemo(() => {
    if (!repoNameValue) return "";
    const repo = repoOptions.find((r: any) => r.full_name === repoNameValue);
    return repo ? repo.id.toString() : "";
  }, [repoNameValue, repoOptions]);

  // Sync selectedRepo state when repoNameValue changes (for back button navigation)
  useEffect(() => {
    if (repoNameValue && !selectedRepo) {
      const repo = repoOptions.find((r: any) => r.full_name === repoNameValue);
      if (repo) {
        setSelectedRepo({
          owner: repo.owner?.login ?? "",
          repoName: repo.full_name,
          name: repo.name,
          installationId: repo.installationId,
        });
      }
    }
  }, [repoNameValue, repoOptions, selectedRepo]);

  const steps = [
    {
      key: "application",
      title: "Application details",
      description: "",
    },
    {
      key: "resources",
      title: "Resources",
      description: "",
    },
    {
      key: "summary",
      title: "Summary",
      description: "",
    },
  ] as const;

  const [currentStep, setCurrentStep] = useState(0);
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const stepValidationFields = useMemo(
    () =>
      ({
        0: [
          "sourceType",
          "repoName",
          "repoUrl",
          "branch",
          "region",
          "env",
          "environmentVariables",
        ],
        1: [
          "deployType",
          ...(deployType === "SHARED_COMPUTE_INSTANCE"
            ? (["port"] as Array<keyof CreateDeployFormData>)
            : []),
        ],
        2: [],
      }) as Record<number, Array<keyof CreateDeployFormData>>,
    [deployType],
  );

  const handleNextStep = async () => {
    const fieldsToValidate = stepValidationFields[currentStep] ?? [];
    if (!fieldsToValidate.length) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
      return;
    }

    const isValid = await trigger(fieldsToValidate as any);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const isInitialLoading =
    repoQuery.isPending || regionQuery.isPending || envQuery.isPending;
  const hasInitialError =
    repoQuery.isError || regionQuery.isError || envQuery.isError;

  const shouldShowGitHubConnectBanner =
    repoQuery.isSuccess && (repoQuery.data?.items?.length ?? 0) === 0;

  const deployBreadcrumbs = useMemo(() => {
    return [
      { label: referrerInfo.label, href: referrerInfo.href },
      { label: "create deploy" },
    ];
  }, [referrerInfo]);

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[70vh]">
        <LoadingSpinner className="h-10 w-10 text-cyan-400" />
      </div>
    );
  }

  if (hasInitialError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 min-h-[70vh] text-center text-slate-200">
        <div>
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p className="text-sm text-slate-400">
            We could not load the data needed to create a deploy. Please try
            again.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            void repoQuery.refetch();
            void regionQuery.refetch();
            void envQuery.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="hidden lg:block border-b border-slate-800 bg-slate-900/90">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-5">
          <Breadcrumb items={deployBreadcrumbs} />
        </div>
      </div>
      <div className="container max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        {shouldShowGitHubConnectBanner && (
          <Alert className="mb-6 border-cyan-500/40 bg-cyan-500/10 text-cyan-100">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <AlertTitle className="text-cyan-100">
                  Connect your GitHub account
                </AlertTitle>
                <AlertDescription className="text-cyan-100/80">
                  Connect GitHub to link your repositories
                </AlertDescription>
              </div>
              <Button
                className="cyber-button self-start sm:self-auto"
                onClick={() => navigate(PATH.GITHUB)}
              >
                Connect GitHub
              </Button>
            </div>
          </Alert>
        )}
        <div className="mb-10">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            {steps.map((step, index) => {
              const isActive = index === currentStep;
              const isComplete = index < currentStep;
              return (
                <button
                  key={step.key}
                  type="button"
                  onClick={() => {
                    if (isComplete || index === currentStep) {
                      setCurrentStep(index);
                    }
                  }}
                  className={`flex flex-col gap-2 rounded-xl border px-4 py-3 text-left transition-colors ${
                    isActive
                      ? "border-cyan-500/80 bg-cyan-500/10"
                      : isComplete
                        ? "border-cyan-500/40 bg-cyan-500/5"
                        : "border-slate-700 bg-slate-900/40 hover:border-slate-500"
                  }`}
                  aria-current={isActive ? "step" : undefined}
                >
                  <div className="flex items-center gap-3 text-sm font-medium uppercase tracking-wide">
                    <span
                      className={`h-6 w-6 rounded-full border flex items-center justify-center ${
                        isActive || isComplete
                          ? "border-cyan-500 text-cyan-300"
                          : "border-slate-600 text-slate-500"
                      }`}
                    >
                      {isComplete ? "✓" : index + 1}
                    </span>
                    <span
                      className={
                        isActive || isComplete
                          ? "text-cyan-200"
                          : "text-slate-400"
                      }
                    >
                      {step.title}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {currentStep === 0 && (
            <div className="space-y-6">
              <Card className="border border-slate-700/50 bg-slate-900/40">
                <CardHeader>
                  <CardTitle className="text-slate-100">
                    Source selection
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() =>
                      setValue("sourceType", "github", { shouldValidate: true })
                    }
                    className={`flex flex-col gap-3 rounded-xl border-2 p-4 text-left transition ${
                      sourceType === "github"
                        ? "border-cyan-500 bg-cyan-500/10"
                        : "border-slate-700 bg-slate-900/40 hover:border-slate-500"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-slate-200">
                        <Github className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-100">
                          Git repository
                        </h3>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    disabled
                    className="flex flex-col gap-3 rounded-xl border-2 border-dashed border-slate-700 p-4 text-left text-slate-500"
                  >
                    <div className="flex items-center gap-3 opacity-60">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-slate-200">
                        <Upload className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-100">
                          Upload your code (coming soon)
                        </h3>
                      </div>
                    </div>
                  </button>
                  {errors.sourceType && (
                    <p className="text-xs text-red-400 md:col-span-2">
                      {errors.sourceType.message}
                    </p>
                  )}
                </CardContent>
              </Card>

              <input type="hidden" {...register("repoName")} />
              <input type="hidden" {...register("repoUrl")} />
              <input type="hidden" {...register("branch")} />
              <input
                type="hidden"
                {...register("installationId", { valueAsNumber: true })}
              />

              <Card className="border border-slate-700/50 bg-slate-900/40">
                <CardHeader>
                  <CardTitle className="text-slate-100">
                    Repository details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="repository">Repository</Label>
                      <Select
                        value={selectedRepoId}
                        onValueChange={(value) => {
                          const repo = repoOptions.find(
                            (r: any) => r.id.toString() === value,
                          );
                          if (repo) {
                            setValue("repoName", repo.full_name, {
                              shouldValidate: true,
                            });
                            setValue("repoUrl", repo.clone_url, {
                              shouldValidate: true,
                            });
                            setValue("installationId", repo.installationId, {
                              shouldValidate: true,
                            });
                            setSelectedRepo({
                              owner: repo.owner?.login ?? "",
                              repoName: repo.full_name,
                              name: repo.name,
                              installationId: repo.installationId,
                            });
                            setValue("branch", "", { shouldValidate: true });
                          }
                        }}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200">
                          <SelectValue
                            placeholder={
                              repoQuery.isLoading
                                ? "Loading repositories..."
                                : "Select a repository"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {repoOptions.map((repo: any) => (
                            <SelectItem
                              key={repo.id}
                              value={repo.id.toString()}
                              className="text-slate-200"
                            >
                              {repo.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.repoName && (
                        <p className="text-red-400 text-xs">
                          {errors.repoName.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="branch">Default branch</Label>
                      <Select
                        value={branchValue}
                        onValueChange={(branch) =>
                          setValue("branch", branch, { shouldValidate: true })
                        }
                        disabled={branchDisabled}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200">
                          <SelectValue
                            placeholder={
                              branchQuery.isLoading
                                ? "Loading branches..."
                                : "Select a branch"
                            }
                          />
                        </SelectTrigger>
                        {!branchQuery.isLoading && branchQuery.data && (
                          <SelectContent className="bg-slate-900 border-slate-700">
                            {branchQuery.data?.map((branch: string) => (
                              <SelectItem
                                key={branch}
                                value={branch}
                                className="text-slate-200"
                              >
                                {branch}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        )}
                      </Select>
                      {errors.branch && (
                        <p className="text-red-400 text-xs">
                          {errors.branch.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="region">Region</Label>
                      <Select
                        defaultValue={regions[0]}
                        onValueChange={(region) =>
                          setValue("region", region, { shouldValidate: true })
                        }
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200">
                          <SelectValue placeholder="Select a region" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {regions.map((region) => (
                            <SelectItem
                              key={region}
                              value={region}
                              className="text-slate-200"
                            >
                              {humanizeString(region)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.region && (
                        <p className="text-red-400 text-xs">
                          {errors.region.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="env">Environment</Label>
                      <Select
                        defaultValue={envs[0]}
                        onValueChange={(env) =>
                          setValue("env", env, { shouldValidate: true })
                        }
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200">
                          <SelectValue placeholder="Select environment" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {envs.map((env) => (
                            <SelectItem
                              key={env}
                              value={env}
                              className="text-slate-200"
                            >
                              {humanizeString(env)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.env && (
                        <p className="text-red-400 text-xs">
                          {errors.env.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator className="my-4 bg-slate-800/50" />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label
                          htmlFor="autoDeployEnabled"
                          className="text-slate-200 font-medium"
                        >
                          Enable auto-deploy
                        </Label>
                        <p className="text-xs text-slate-400">
                          Automatically trigger a new deployment when you push
                          code to this branch
                        </p>
                      </div>
                      <Switch
                        id="autoDeployEnabled"
                        checked={watch("autoDeployEnabled")}
                        onCheckedChange={(checked) =>
                          setValue("autoDeployEnabled", checked, {
                            shouldValidate: true,
                          })
                        }
                        className="data-[state=checked]:bg-cyan-500"
                      />
                    </div>
                    {errors.autoDeployEnabled && (
                      <p className="text-red-400 text-xs">
                        {errors.autoDeployEnabled.message}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-700/50 bg-slate-900/40">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-slate-100">
                        Environment variables
                      </CardTitle>
                    </div>
                    <Button
                      type="button"
                      onClick={addEnvVar}
                      size="sm"
                      className="cyber-button"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add variable
                    </Button>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="pr-2 max-h-[300px] overflow-y-auto">
                    {fields.length === 0 && (
                      <EnvPasteArea
                        onPasteVars={(vars) =>
                          replace(
                            vars.map((v) => ({ key: v.key, value: v.value })),
                          )
                        }
                      />
                    )}

                    {fields.map((field, index) => (
                      <div key={field.id}>
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1 min-w-0 space-y-2 pl-1">
                            <Label className="text-xs text-slate-300">
                              KEY
                            </Label>
                            <Input
                              {...register(
                                `environmentVariables.${index}.key` as const,
                              )}
                              placeholder="key"
                              className="w-full bg-slate-900 border-slate-700 text-slate-100 focus:border-cyan-400 "
                            />
                            {errors.environmentVariables?.[index]?.key && (
                              <p className="text-red-400 text-xs">
                                {
                                  errors.environmentVariables[index]?.key
                                    ?.message as string
                                }
                              </p>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 space-y-2">
                            <Label className="text-xs text-slate-300">
                              VALUE
                            </Label>
                            <Input
                              {...register(
                                `environmentVariables.${index}.value` as const,
                              )}
                              placeholder="value"
                              className="w-full bg-slate-900 border-slate-700 text-slate-100 focus:border-cyan-400"
                            />
                          </div>
                          <Button
                            type="button"
                            onClick={() => remove(index)}
                            variant="ghost"
                            size="icon"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 sm:self-end"
                            aria-label={`Remove variable ${index + 1}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        {index < fields.length - 1 && (
                          <Separator className="mt-4 bg-slate-800/50" />
                        )}
                      </div>
                    ))}

                    {fields.length > 0 && (
                      <div className="pt-4 border-t border-slate-800/50">
                        <EnvPasteArea
                          onPasteVars={(vars) => {
                            vars.forEach((envVar) =>
                              append({ key: envVar.key, value: envVar.value }),
                            );
                          }}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-6">
              <Card className="border border-slate-700/50 bg-slate-900/40">
                <CardHeader>
                  <CardTitle className="text-slate-100">
                    Deployment type
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Decide whether this deployment serves a static site or a
                    running server.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() =>
                      handleDeployTypeChange("SHARED_COMPUTE_INSTANCE")
                    }
                    className={`flex flex-col gap-3 rounded-xl border-2 p-4 text-left transition ${
                      deployType === "SHARED_COMPUTE_INSTANCE"
                        ? "border-cyan-500 bg-cyan-500/10"
                        : "border-slate-700 bg-slate-900/40 hover-border-slate-500"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-cyan-300">
                        <Server className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-100">
                          Server app
                        </h3>
                        <p className="text-xs text-slate-400">
                          Runs a long-lived web process. Perfect for APIs and
                          SSR apps.
                        </p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeployTypeChange("CDN_SITE")}
                    className={`flex flex-col gap-3 rounded-xl border-2 p-4 text-left transition ${
                      deployType === "CDN_SITE"
                        ? "border-cyan-500 bg-cyan-500/10"
                        : "border-slate-700 bg-slate-900/40 hover-border-slate-500"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-cyan-300">
                        <Globe className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-100">
                          Static site
                        </h3>
                        <p className="text-xs text-slate-400">
                          Ideal for SPA frameworks or generated sites served
                          over CDN.
                        </p>
                      </div>
                    </div>
                  </button>
                  {errors.deployType && (
                    <p className="text-xs text-red-400 md:col-span-2">
                      {errors.deployType.message}
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card className="border border-slate-700/50 bg-slate-900/40">
                <CardHeader>
                  <CardTitle className="text-slate-100">
                    Set up your web process
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Provide runtime information for your application.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {deployType === "SHARED_COMPUTE_INSTANCE" ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="port">Port</Label>
                        <Input
                          id="port"
                          type="text"
                          placeholder="8080"
                          {...register("port", {
                            setValueAs: (v) =>
                              v === "" ? undefined : parseInt(v, 10),
                          })}
                          className="bg-slate-900 border-slate-700 text-slate-100 focus:border-cyan-400"
                        />
                        {errors.port && (
                          <p className="text-red-400 text-xs">
                            {errors.port.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="buildCommand">
                          Start command (optional)
                        </Label>
                        <Input
                          id="buildCommand"
                          {...register("buildCommand")}
                          placeholder="npm start"
                          className="bg-slate-900 border-slate-700 text-slate-100 focus:border-cyan-400"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="rootDirectory">
                          Root directory (optional)
                        </Label>
                        <Input
                          id="rootDirectory"
                          placeholder="."
                          {...register("rootDirectory")}
                          className="bg-slate-900 border-slate-700 text-slate-100 focus:border-cyan-400"
                        />
                      </div>

                      <div className="rounded-lg border border-slate-700/50 bg-slate-900/50 p-4">
                        <p className="text-sm font-medium text-slate-200">
                          Compute
                        </p>
                        <p className="text-xs text-slate-500">
                          Selection coming soon. We'll start you on 0.5 CPU / 1
                          GB RAM automatically.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="rootDirectory">
                          Root directory (optional)
                        </Label>
                        <Input
                          id="rootDirectory"
                          placeholder="."
                          {...register("rootDirectory")}
                          className="bg-slate-900 border-slate-700 text-slate-100 focus:border-cyan-400"
                        />
                        <p className="text-xs text-slate-400">
                          Specify the directory containing your code (e.g. src)
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="buildPath">Build path (optional)</Label>
                        <Input
                          id="buildPath"
                          placeholder="dist"
                          {...register("outputDirectory")}
                          className="bg-slate-900 border-slate-700 text-slate-100 focus:border-cyan-400"
                        />
                        <p className="text-xs text-slate-400">
                          Specify the output directory where your built files
                          are located (e.g. dist, build)
                        </p>
                      </div>
                      <div className="rounded-lg border border-slate-700/50 bg-slate-900/50 p-4">
                        <p className="text-sm font-medium text-slate-200">
                          Static deployments
                        </p>
                        <p className="text-xs text-slate-500">
                          Your files will be served from our CDN with automatic
                          caching and global distribution.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          {currentStep === 2 && (
            <Card className="border border-slate-700/50 bg-slate-900/40">
              <CardHeader>
                <CardTitle className="text-slate-100">Summary</CardTitle>
                <CardDescription className="text-slate-400">
                  Confirm the configuration before creating the deployment.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-slate-700/50 bg-slate-900/50 p-4">
                  <h3 className="text-sm font-semibold text-slate-200">
                    Repository
                  </h3>
                  <p className="text-sm text-slate-400">
                    {selectedRepo?.repoName ||
                      watch("repoName") ||
                      "Not selected"}
                  </p>
                  <p className="text-xs text-slate-500">
                    Branch: {branchValue || "Not selected"}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-700/50 bg-slate-900/50 p-4">
                  <h3 className="text-sm font-semibold text-slate-200">
                    Environment
                  </h3>
                  <p className="text-sm text-slate-400">
                    Region:{" "}
                    {regionValue ? humanizeString(regionValue) : "Not selected"}
                  </p>
                  <p className="text-sm text-slate-400">
                    Environment:{" "}
                    {envValue ? humanizeString(envValue) : "Not selected"}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-700/50 bg-slate-900/50 p-4">
                  <h3 className="text-sm font-semibold text-slate-200">
                    Build setup
                  </h3>
                  <p className="text-sm text-slate-400">
                    Deploy type:{" "}
                    {deployType === "CDN_SITE" ? "Static site" : "Server app"}
                  </p>
                  {rootDirectoryValue && (
                    <p className="text-sm text-slate-400">
                      Root directory: {rootDirectoryValue}
                    </p>
                  )}
                  {deployType === "CDN_SITE" ? (
                    <p className="text-sm text-slate-400">
                      Build path: {outputDirValue || "dist"}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-400">
                      Port: {portValue ?? "8080"}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-between gap-4 pt-4">
            <Button
              type="button"
              variant="secondary"
              disabled={isCreating || isFirstStep}
              onClick={handlePreviousStep}
              className="text-slate-400 hover:text-cyan-300 hover:bg-cyan-500/10 border border-slate-700 hover:border-cyan-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </Button>
            <div className="flex gap-3">
              {!isLastStep && (
                <Button
                  type="button"
                  disabled={isCreating}
                  onClick={handleNextStep}
                  className="cyber-button"
                >
                  Continue
                </Button>
              )}
              {isLastStep && (
                <Button
                  type="submit"
                  disabled={isCreating}
                  className="cyber-button"
                >
                  {isCreating && (
                    <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                  )}
                  {isCreating ? "Creating..." : "Create deployment"}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
